import { useEffect, useLayoutEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useMatches,
  type RouteObject,
} from "react-router-dom";
import { useAccountStore } from "./store/accountStore";
import { ACCESS_FALLBACK_PATH, canAccessPage, HOME_PATH } from "./accessControl";
import { MainLayout, PageErrorBoundary } from "@/components/layouts";
import { usePermission } from "@/hooks/usePermission";
import { lazyRoute } from "./utils/lazyRoute";

const BASE_TITLE = "ChargeMate";

function DocumentTitle() {
  const matches = useMatches();
  const currentMatch = [...matches]
    .reverse()
    .find((m) => (m.handle as { title?: string })?.title);

  useEffect(() => {
    const title = (currentMatch?.handle as { title?: string })?.title;
    document.title = title ? `${BASE_TITLE} - ${title}` : BASE_TITLE;
  }, [currentMatch]);

  return null;
}

/**
 * 페이지 이동 시 스크롤을 맨 위로 되돌린다. 각 페이지가 자기 컨테이너에서
 * overflow-y-auto 로 스크롤하는 구조라 라우터의 기본 동작만으로는 리셋되지 않는다
 */
function ScrollToTop() {
  const { pathname } = useLocation();

  // 페인트 전에 리셋해야 이전 페이지가 스크롤된 채로 잠깐 보이는 깜빡임이 없다
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document
      .querySelectorAll<HTMLElement>(
        '[data-slot="sidebar-inset"] .overflow-y-auto, [data-slot="sidebar-inset"] .overflow-auto',
      )
      .forEach((el) => {
        el.scrollTop = 0;
      });
  }, [pathname]);

  return null;
}

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { access_token } = useAccountStore();

  return access_token ? (
    <MainLayout>
      <DocumentTitle />
      <ScrollToTop />
      {children}
    </MainLayout>
  ) : (
    <Navigate to="/" replace />
  );
};

// 현재 경로가 로그인 계정의 회사 유형에 허용되지 않으면 기본 페이지로 리다이렉트.
// 페이지별 접근 정책은 accessControl 의 PAGE_ACCESS_BY_TYPE 단일 출처에서 관리한다.
const AccessGuard = ({ children }: { children: React.ReactNode }) => {
  const { affiliationType } = usePermission();
  const { pathname } = useLocation();

  if (!canAccessPage(affiliationType, pathname)) {
    return <Navigate to={ACCESS_FALLBACK_PATH} replace />;
  }

  return <>{children}</>;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { access_token } = useAccountStore();

  if (access_token) return <Navigate to={HOME_PATH} replace />;

  return (
    <>
      <DocumentTitle />
      {children}
    </>
  );
};

const Login = lazyRoute(() =>
  import("@/pages/login").then((module) => ({ default: module.Login })),
);

const SignUp = lazyRoute(() =>
  import("@/pages/signUp").then((module) => ({ default: module.SignUp })),
);

const Station = lazyRoute(() =>
  import("@/pages/station").then((module) => ({ default: module.Station })),
);

const Charger = lazyRoute(() =>
  import("@/pages/charger").then((module) => ({ default: module.Charger })),
);

const Roaming = lazyRoute(() =>
  import("@/pages/roaming").then((module) => ({ default: module.Roaming })),
);

const PricePolicy = lazyRoute(() =>
  import("@/pages/pricePolicy").then((module) => ({
    default: module.PricePolicy,
  })),
);

const PricePolicyCreate = lazyRoute(() =>
  import("@/pages/pricePolicy").then((module) => ({
    default: module.PricePolicyCreate,
  })),
);

const PricePolicyReservation = lazyRoute(() =>
  import("@/pages/pricePolicy").then((module) => ({
    default: module.PricePolicyReservation,
  })),
);

const Dashboard = lazyRoute(() =>
  import("@/pages/dashboard").then((module) => ({ default: module.Dashboard })),
);

const ChargingHistory = lazyRoute(() =>
  import("@/pages/chargingHistory").then((module) => ({
    default: module.ChargingHistory,
  })),
);

const ChargingAuthLog = lazyRoute(() =>
  import("@/pages/chargingAuthLog").then((module) => ({
    default: module.ChargingAuthLog,
  })),
);

const UsageHistory = lazyRoute(() =>
  import("@/pages/usageHistory").then((module) => ({
    default: module.UsageHistory,
  })),
);

const UsageSummary = lazyRoute(() =>
  import("@/pages/usageSummary").then((module) => ({
    default: module.UsageSummary,
  })),
);

const Payment = lazyRoute(() =>
  import("@/pages/payment").then((module) => ({
    default: module.Payment,
  })),
);

const ChargerLog = lazyRoute(() =>
  import("@/pages/chargerLog").then((module) => ({
    default: module.chargerLog,
  })),
);

const Revenue = lazyRoute(() =>
  import("@/pages/revenue").then((module) => ({ default: module.Revenue })),
);

const AuthChargerCard = lazyRoute(() =>
  import("@/pages/authChargerCard").then((module) => ({
    default: module.AuthChargerCard,
  })),
);

const Operator = lazyRoute(() =>
  import("@/pages/operator").then((module) => ({ default: module.Operator })),
);

const Client = lazyRoute(() =>
  import("@/pages/client").then((module) => ({ default: module.Client })),
);

const Account = lazyRoute(() =>
  import("@/pages/account").then((module) => ({ default: module.Account })),
);

const CsInquiry = lazyRoute(() =>
  import("@/pages/csInquiry").then((module) => ({
    default: module.CsInquiry,
  })),
);

const MaintenanceTicket = lazyRoute(() =>
  import("@/pages/maintenanceTicket").then((module) => ({
    default: module.MaintenanceTicket,
  })),
);

const PriceInfo = lazyRoute(() =>
  import("@/pages/priceInfo").then((module) => ({
    default: module.PriceInfo,
  })),
);

const routes: RouteObject[] = [
  // 로그인 여부와 무관하게 여는 공개 화면 (QR 진입)
  {
    path: "/price-info/:chargerId?",
    element: (
      <>
        <DocumentTitle />
        <PriceInfo />
      </>
    ),
    handle: { title: "충전 요금 안내" },
    errorElement: <PageErrorBoundary />,
  },
  {
    path: "/",
    element: (
      <PublicRoute>
        <Outlet />
      </PublicRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="login" replace />,
      },
      {
        path: "login",
        element: <Login />,
        handle: { title: "로그인" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "signUp",
        element: <SignUp />,
        handle: { title: "회원가입" },
        errorElement: <PageErrorBoundary />,
      },
    ],
  },
  {
    path: "/",
    element: (
      <PrivateRoute>
        <AccessGuard>
          <Outlet />
        </AccessGuard>
      </PrivateRoute>
    ),
    errorElement: <PageErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to={HOME_PATH} replace />,
      },
      {
        path: "dashboard",
        element: <Dashboard />,
        handle: { title: "대시보드" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "charging-history",
        element: <ChargingHistory />,
        handle: { title: "충전 내역" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "charging-auth-log",
        element: <ChargingAuthLog />,
        handle: { title: "충전 인증 내역" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "usage-history",
        element: <UsageHistory />,
        handle: { title: "이용 내역" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "payment",
        element: <Payment />,
        handle: { title: "결제 내역" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "station",
        element: <Station />,
        handle: { title: "충전소 관리" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "charger",
        element: <Charger />,
        handle: { title: "충전기 관리" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "roaming",
        element: <Roaming />,
        handle: { title: "로밍 관리" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "price-policy",
        element: <PricePolicy />,
        handle: { title: "요금 관리" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "price-policy/create",
        element: <PricePolicyCreate />,
        handle: { title: "요금제 등록" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "price-policy/:uuid/reservation",
        element: <PricePolicyReservation />,
        handle: { title: "요금제 변경 예약" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "charger-log",
        element: <ChargerLog />,
        handle: { title: "충전기 로그" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "revenue",
        element: <Revenue />,
        handle: { title: "매출 집계" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "usage-summary",
        element: <UsageSummary />,
        handle: { title: "이용 집계" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "charger-card",
        element: <AuthChargerCard />,
        handle: { title: "충전카드 관리" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "operator",
        element: <Operator />,
        handle: { title: "운영사 관리" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "client",
        element: <Client />,
        handle: { title: "고객사 관리" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "account",
        element: <Account />,
        handle: { title: "계정 관리" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "cs-inquiry",
        element: <CsInquiry />,
        handle: { title: "CS 관리" },
        errorElement: <PageErrorBoundary />,
      },
      {
        path: "maintenance-ticket",
        element: <MaintenanceTicket />,
        handle: { title: "유지보수 관리" },
        errorElement: <PageErrorBoundary />,
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
